import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/atoms';
import ChoiceButton from '../../ui/molecules/ChoiceButton';
import { LOCAL_WORKER_API_BASE } from '../../api';

const userCounts = [
  { value: '0-100', label: '0-100' },
  { value: '100-1000', label: '100-1,000' },
  { value: '1000-10000', label: '1,000-10,000' },
  { value: '10000+', label: '10,000+' },
];

const languages = [
  { value: 'en-US', label: 'English' },
  { value: 'pl-PL', label: 'Polski' }
];

// Input sanitization helper
const sanitizeInput = (input, maxLength = 100) => {
  if (typeof input !== 'string') return '';
  return input
    .replace(/[<>{}]/g, '') // Remove potentially dangerous characters
    .substring(0, maxLength);
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getOnboardingApiBases = () => {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  return isLocalhost
    ? ['/workers/api', window.location.origin + '/api', LOCAL_WORKER_API_BASE, `${process.env.REACT_APP_ARTICLE_API_PROXY_TARGET || 'http://localhost:3001'}/api`]
    : ['https://api.prosecaptain.com/workers/api'];
};

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [language, setLanguage] = useState('en-US');
  const [userCount, setUserCount] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [setupStageIndex, setSetupStageIndex] = useState(0);

  const steps = [
    { title: 'Personal Information', description: 'Tell us your name' },
    { title: 'Workspace Setup', description: 'Let\'s set up your workspace' },
    { title: 'Language Preference', description: 'Choose your preferred language' },
    { title: 'User Base', description: 'How many monthly users do you have?' },
  ];
  const setupStages = useMemo(() => ([
    'Aktywowanie konta',
    'Tworzenie agenta',
    'Konfigurowanie przestrzeni roboczej',
    'Finalizowanie ustawień'
  ]), []);
  const isFinalStep = step === steps.length - 1;
  const isFinalizing = isFinalStep && isSubmitting;

  useEffect(() => {
    if (!isFinalizing) {
      setSetupStageIndex(0);
      return;
    }

    const intervalId = setInterval(() => {
      setSetupStageIndex((prev) => (prev + 1) % setupStages.length);
    }, 1200);

    return () => clearInterval(intervalId);
  }, [isFinalizing, setupStages.length]);

  const handleNext = () => {
    setError('');
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep(step - 1);
      setError('');
    }
  };

  const handleComplete = async () => {
    try {
      setIsSubmitting(true);
      setError('');
      const token = localStorage.getItem('directus_token');

      if (!token) {
        throw new Error('User not authenticated');
      }

      const payload = {
        firstName,
        lastName,
        workspaceName,
        organizationName: workspaceName, // backward compatibility for existing backend contract
        websiteUrl: websiteUrl.trim(),
        language,
        userCount,
        // Legacy fields kept for backward compatibility with older onboarding API validation.
        teamSize: userCount || 'not_specified',
        position: 'not_specified',
        goals: ['not_specified'],
        selectedGoals: ['not_specified']
        // Note: userId is now extracted from the token on the server side for security
      };

      let response = null;
      let lastNetworkError = null;
      const bases = getOnboardingApiBases();

      for (const base of bases) {
        let shouldTryNextBase = false;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            response = await fetch(`${base}/onboarding/complete`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(payload)
            });
            if (response.status === 404) {
              shouldTryNextBase = true;
              response = null;
              break;
            }
            if (response.status !== 429 || attempt === 2) break;
            await sleep(800 * (attempt + 1));
          } catch (error) {
            lastNetworkError = error;
            response = null;
            shouldTryNextBase = true;
            break;
          }
        }
        if (shouldTryNextBase) continue;
        if (response) break;
      }

      if (!response) {
        if (lastNetworkError instanceof Error) {
          throw lastNetworkError;
        }
        throw new Error('Failed to complete onboarding');
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        if (response.status === 429) {
          throw new Error('Server is busy. Please wait a few seconds and try again.');
        }
        throw new Error(data?.error || data?.message || 'Failed to complete onboarding');
      }

      navigate('/');
    } catch (error) {
      if (error instanceof Error) {
        setError(`Failed to complete onboarding: ${error.message}`);
      } else {
        setError('Failed to complete onboarding');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                id="firstName"
                className="form-input py-2"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(sanitizeInput(e.target.value, 50))}
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                id="lastName"
                className="form-input py-2"
                placeholder="Smith"
                value={lastName}
                onChange={(e) => setLastName(sanitizeInput(e.target.value, 50))}
                maxLength={50}
              />
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label htmlFor="workspaceName" className="block text-sm font-medium text-gray-700 mb-2">
                Workspace Name
              </label>
              <input
                type="text"
                name="workspaceName"
                id="workspaceName"
                className="form-input py-2"
                placeholder="My Workspace"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(sanitizeInput(e.target.value, 100))}
                maxLength={100}
              />
            </div>
            {/* TODO: optional NIP field; the backend will fetch company data from CEIDG. */}
            <div>
              <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700 mb-2">
                Website <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <input
                type="url"
                inputMode="url"
                name="websiteUrl"
                id="websiteUrl"
                className="form-input py-2"
                placeholder="example.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(sanitizeInput(e.target.value, 500))}
                maxLength={500}
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            {languages.map((lang) => (
              <ChoiceButton
                key={lang.value}
                selected={language === lang.value}
                onClick={() => setLanguage(lang.value)}
              >
                {lang.label}
              </ChoiceButton>
            ))}
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            {userCounts.map((count) => (
              <ChoiceButton
                key={count.value}
                selected={userCount === count.value}
                onClick={() => setUserCount(count.value)}
              >
                {count.label}
              </ChoiceButton>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed z-50 inset-0 bg-gray-100 flex flex-col justify-center items-center p-4 md:p-8">
      <div className="w-full max-w-4xl bg-white py-8 px-6 rounded-lg  mb-16">
        <div className="mb-8 absolute top-0 w-full left-0">
          <div className="overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full bg-primary-500 transition-all duration-500 ease-in-out"
              style={{ width: `${((step + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="px-4 py-16 sm:p-6">
          {isFinalizing ? (
            <div className="max-w-2xl mx-auto">
              <h2 className="text-center text-3xl font-medium text-primary-900">
                Kończymy konfigurację
              </h2>
              <p className="text-center text-xl mt-2 text-neutral-800 mb-8">
                Przygotowujemy Twoje środowisko. To potrwa chwilę.
              </p>

              <div className="rounded-xl border border-gray-200 p-6 bg-gray-50">
                <div className="flex items-center mb-5">
                  <svg className="animate-spin mr-3 h-5 w-5 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  <p className="text-base font-medium text-gray-900">
                    {setupStages[setupStageIndex]}
                  </p>
                </div>

                <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary-500 transition-all duration-700 ease-out"
                    style={{ width: `${Math.min(((setupStageIndex + 1) / setupStages.length) * 100, 95)}%` }}
                  />
                </div>

                <div className="mt-4 space-y-2">
                  {setupStages.map((stage, idx) => (
                    <div key={stage} className="flex items-center text-sm">
                      <span className={`mr-2 inline-block h-2 w-2 rounded-full ${
                        idx <= setupStageIndex ? 'bg-primary-500' : 'bg-gray-300'
                      }`} />
                      <span className={idx <= setupStageIndex ? 'text-gray-800' : 'text-gray-500'}>
                        {stage}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
            </div>
          ) : (
            <>
              <h2 className="text-center text-3xl font-medium text-primary-900">
                {steps[step].title}
              </h2>
              <p className="text-center text-xl mt-2 text-neutral-800 mb-8">{steps[step].description}</p>
              {renderStep()}
              {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              <div className="mt-8 flex justify-between">
                <Button
                  onClick={handlePrev}
                  disabled={step === 0 || isSubmitting}
                  variant="primary-outline"
                  size="md"
                  className={`rounded-md ${step === 0 || isSubmitting ? 'border-gray-300 text-gray-500 bg-gray-100' : ''}`}
                >
                  <ChevronLeftIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  Previous
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={isSubmitting}
                  variant="primary"
                  size="md"
                  className="rounded-md disabled:cursor-not-allowed"
                >
                  {step < steps.length - 1 ? (
                    <>
                      Next
                      <ChevronRightIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
                    </>
                  ) : (
                    'Complete'
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;